<template>
    <div class="overflow-hidden rounded-lg bg-white ring-1 ring-black/5 shadow">
        <table class="w-full text-sm">
            <thead class="bg-gray-50 text-gray-500">
                <tr>
                    <th class="px-6 py-3" />
                    <th v-for="column in columns" :key="column.key" class="px-6 py-3 text-right font-medium">
                        {{ column.label }}
                    </th>
                </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
                <tr v-for="row in rows" :key="row.key">
                    <th class="px-6 py-3 text-left font-medium text-gray-900">
                        {{ row.label }}
                        <span v-if="row.caption" class="block text-xs font-normal text-gray-400">{{ row.caption }}</span>
                    </th>
                    <td
                        v-for="column in columns"
                        :key="column.key"
                        class="px-6 py-3 text-right tabular-nums"
                        :class="cellClass(row, column.key)"
                        :title="isLowSample(column.key) ? 'Low sample' : undefined"
                    >
                        <span class="font-semibold">{{ row.value(current[column.key]) }}</span>
                        <template v-if="column.key === 'all'">
                            <span v-if="row.sub?.(current.all)" class="block text-xs text-gray-400">{{ row.sub(current.all) }}</span>
                            <span v-if="deltaFor(row)" class="block text-xs" :class="TONE_TEXT[deltaFor(row)!.tone]">{{ deltaFor(row)!.label }}</span>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
import type { HandlingCell, HandlingKey } from '~/types/dashboard'
import {
    HANDLING_BUCKETS, LOW_SAMPLE_MIN, TONE_TEXT, formatDuration, durationDelta, pointsDelta, worstBucket,
    type Delta, type TimedMetric,
} from '~/utils/dashboardStats'

interface Row {
    key: string
    label: string
    caption?: string
    value: (cell: HandlingCell) => string
    sub?: (cell: HandlingCell) => string | null
    delta?: (current: HandlingCell, previous: HandlingCell) => Delta | null
    metric?: TimedMetric
}

const props = defineProps<{
    current: Record<HandlingKey, HandlingCell>
    previous: HandlingCell
}>()

const columns: { key: HandlingKey; label: string }[] = [{ key: 'all', label: 'All' }, ...HANDLING_BUCKETS]

const rows: Row[] = [
    {
        key: 'calls',
        label: 'Calls',
        value: (c) => String(c.calls),
    },
    {
        key: 'tta',
        label: 'Time to answer',
        caption: 'Average',
        value: (c) => formatDuration(c.tta_avg_s),
        delta: (c, p) => durationDelta(c.tta_avg_s, p.tta_avg_s, 'lower'),
        metric: 'tta_avg_s',
    },
    {
        key: 'talk',
        label: 'Talk time',
        caption: 'Median, excluding hold',
        value: (c) => formatDuration(c.talk_median_s),
        delta: (c, p) => durationDelta(c.talk_median_s, p.talk_median_s, 'neutral'),
        metric: 'talk_median_s',
    },
    {
        key: 'held',
        label: 'Put on hold',
        caption: 'Calls · % of answered',
        value: (c) => (c.held_pct == null ? '—' : `${c.held_calls} · ${c.held_pct}%`),
        delta: (c, p) => pointsDelta(c.held_pct, p.held_pct),
        metric: 'held_pct',
    },
    {
        key: 'hold',
        label: 'Hold time',
        caption: 'Average per held call',
        value: (c) => formatDuration(c.hold_avg_s),
        sub: (c) => (c.hold_max_s == null ? null : `longest ${formatDuration(c.hold_max_s)}`),
        delta: (c, p) => durationDelta(c.hold_avg_s, p.hold_avg_s, 'lower'),
        metric: 'hold_avg_s',
    },
]

const worst = computed(() =>
    Object.fromEntries(rows.filter((r) => r.metric).map((r) => [r.key, worstBucket(props.current, r.metric!)])),
)

function isLowSample(key: HandlingKey) {
    return key !== 'all' && props.current[key].calls < LOW_SAMPLE_MIN
}

function cellClass(row: Row, key: HandlingKey) {
    if (isLowSample(key)) return 'text-gray-300'
    if (worst.value[row.key] === key) return 'bg-amber-50 text-gray-900'
    return 'text-gray-900'
}

function deltaFor(row: Row) {
    return row.delta?.(props.current.all, props.previous) ?? null
}
</script>
